import React from 'react';

import { WorkerDetails } from '@vyce/core/src/views/time/WorkerDetails';
import { useEmployeeProfileData } from '@vyce/core/src/modules/userPreviewModule/hooks/useEmployeeProfileData';
import { deleteCustomQualificationRequest, getCheckersRequest } from '@vyce/core/src/api/checkers';
import { getTagListRequest } from '@vyce/core/src/api/handbook';
import { getCompanyUserCoreRequest } from '@vyce/core/src/api/companies';
import {
  getHealthAndSafetyPDFByUserIdRequest,
  getPassportRequest,
  getRtwRequest,
  createCustomQualificationRequest,
  updateQualificationRequest,
  createQualificationRequest,
  uploadCustomQualificationImageRequest,
  deleteCustomQualificationImageRequest,
} from '@vyce/core/src/api/teams';
import { UserPreviewProvider } from '@vyce/core/src/contexts/userPreviewProvider';
import { QualificationDialog } from '@vyce/core/src/modules/qualificationDialog';
import { UserPreview } from '@vyce/core/src/views/preview/user/UserPreview';
import { ChatUser, CustomCertificate, User } from '@vyce/core/src/types';
import useChatRequests from '@vyce/core/src/hooks/useChatRequests';

import { useTypedSelector } from '../../hooks';

export const EmployerWorkerDetails: React.FC = () => {
  const { selectedCompanyAppData, selectedCompany } = useTypedSelector(state => state.helper);
  const {
    userPreviewData,
    rtw,
    passport,
    userPreviewDataLoading,
    userId,
    selectedCompanyId,
    hasPermissionForCreateQualification,
    showTimeSection,
    isShowAgencyData,
    contextOptions,
    getUserPreviewData,
  } = useEmployeeProfileData({
    getEmployeePreviewRequest: getCompanyUserCoreRequest,
    getPassportRequest,
    getRtwRequest,
    selectedCompany,
  });
  const { first_name, last_name, email, phone, photo, uuid } = useTypedSelector(state => state.user);
  const { createPrivateChat } = useChatRequests();
  const chatCreator: ChatUser = {
    uid: uuid,
    name: `${first_name} ${last_name}`,
    email,
    phone,
    avatarUrl: photo?.url,
  };

  const goToChat = async (userData: User) => {
    if (!chatCreator || !selectedCompany?.uuid) {
      return;
    }
    await createPrivateChat({
      creator: chatCreator,
      chatUser: {
        uid: userData.uuid,
        name: `${userData.first_name} ${userData.last_name}`,
        email: userData.email,
        phone: userData.phone,
        avatarUrl: userData.photo?.url,
      },
      companyId: selectedCompany?.uuid,
    });
  };

  return (
    <UserPreviewProvider value={contextOptions}>
      <WorkerDetails
        loading={userPreviewDataLoading}
        companyId={selectedCompany?.uuid}
        locations={selectedCompanyAppData?.locations || []}
        rtw={rtw}
        passport={passport}
        userPreviewData={userPreviewData}
        isLegend={false}
        showTimeSection={showTimeSection}
        renderProfileContent={(userData: User) => {
          return userData || userPreviewData ? (
            <UserPreview
              rtw={rtw}
              passport={passport}
              userPreviewData={
                userPreviewData || {
                  user: userData,
                }
              }
              createPrivateChat={() => goToChat(userData)}
              userId={userId}
              companyId={selectedCompany?.uuid ?? ''}
              deleteCustomQualificationRequest={deleteCustomQualificationRequest}
              showHealthAndSafetyPDFRequest={getHealthAndSafetyPDFByUserIdRequest}
              withAddQualificationModal={true}
              hasPermissionForCreateQualification={hasPermissionForCreateQualification}
              isShowAgencyData={isShowAgencyData}
              renderQualificationModal={({
                isOpen,
                selectedQualification,
                onClose,
              }: {
                isOpen: boolean;
                selectedQualification: CustomCertificate;
                onClose: () => void;
              }) => (
                <QualificationDialog
                  isAdmin={false}
                  isOpen={isOpen}
                  onClose={onClose}
                  selectedQualification={selectedQualification as CustomCertificate}
                  reRequestData={getUserPreviewData}
                  getCheckersRequest={getCheckersRequest}
                  triggerCheckerRequest={createQualificationRequest}
                  createCustomQualificationRequest={createCustomQualificationRequest}
                  updateCustomQualificationRequest={updateQualificationRequest}
                  uploadCustomQualificationImageRequest={uploadCustomQualificationImageRequest}
                  deleteCustomQualificationImageRequest={deleteCustomQualificationImageRequest}
                  getTagListRequest={getTagListRequest}
                  type="outside"
                  userId={userId}
                  selectedCompanyId={selectedCompanyId}
                />
              )}
            />
          ) : null;
        }}
      />
    </UserPreviewProvider>
  );
};
